import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router';
import styled, { keyframes } from 'styled-components';
import { itemsAPI } from '../../api/itemsAPI';
import {
  getArtistItemPublicPath,
  getBoutiqueItemPublicPath,
  getDesignerItemPublicPath,
  getMarketplaceItemDetailPath,
  getListingLinkState,
} from '../../utils/itemLinks';
import { formatListingPriceLabel } from '../../utils/formatPrice';
import ItemFavoriteButton from './ItemFavoriteButton';
import { resolveListingBrand } from './ListingDetailShared';
import { getFavoriteTargetFromFeaturedRow } from '../../utils/favoriteKeys';
import { sizedImageUrl } from '../../utils/listingImages';
// Admin-curated horizontal strip rendered at the top of:
//   /items                 → scope="regular_items"
//   /defected-items        → scope="defected_items"
//   /designers             → scope="designer_items"
// Reads from `GET /api/public/featured/?scope=...`. Hides itself silently
// if the admin hasn't curated anything yet — no empty placeholder noise.

const Section = styled.section`
  margin: 0 0 0.15rem;
  padding: 0.1rem 0;
  background: transparent;
  border: none;
  width: 100%;
  /* Reserve strip height while loading to avoid CLS when featured items arrive */
  min-height: 340px;
  box-sizing: border-box;
`;

const Header = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 0.25rem;
  min-height: 1.2rem;
`;

const Title = styled.h2`
  font-family: 'Cormorant Garamond', Georgia, serif;
  font-size: clamp(1.35rem, 2.4vw, 1.85rem);
  font-weight: 600;
  font-style: italic;
  color: #14532d;
  margin: 0 0 1rem;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 0.5rem;
  letter-spacing: 0;
  text-align: center;
  width: 100%;
`;

const Strip = styled.div`
  display: flex;
  gap: 0.75rem;
  overflow-x: auto;
  scroll-snap-type: x mandatory;
  scroll-behavior: smooth;
  -webkit-overflow-scrolling: touch;
  padding-bottom: 0.35rem;

  &::-webkit-scrollbar {
    display: none;
  }
`;

const Card = styled(Link)`
  flex: 0 0 200px;
  scroll-snap-align: start;
  text-decoration: none;
  color: inherit;
  background: transparent;
  border: none;
  overflow: hidden;
  display: flex;
  flex-direction: column;
  transition: opacity 0.15s ease;

  &:hover {
    opacity: 0.92;
  }

  @media (max-width: 600px) {
    flex: 0 0 46vw;
    max-width: 200px;
  }
`;

const Thumb = styled.div`
  width: 100%;
  aspect-ratio: 3 / 4;
  background: var(--bg-secondary);
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 1.75rem;
  color: var(--text-light);
  position: relative;
  overflow: hidden;
  contain: layout paint;
`;

const ThumbImg = styled.img`
  width: 100%;
  height: 100%;
  object-fit: cover;
  object-position: center;
  display: block;
  transform: scale(1.1);
`;

const Body = styled.div`
  padding: 0.45rem 0.1rem 0.2rem;
  display: flex;
  flex-direction: column;
  gap: 0.15rem;
`;

const Price = styled.div`
  font-size: 0.9rem;
  font-weight: 700;
  color: var(--text-primary);
`;

const Meta = styled.div`
  font-size: 0.72rem;
  color: var(--text-secondary);
  display: flex;
  align-items: center;
  gap: 0.25rem;
  min-width: 0;
`;

const MetaBrand = styled.span`
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  min-width: 0;
`;

const shimmer = keyframes`
  0% { background-position: -400px 0; }
  100% { background-position: 400px 0; }
`;

const SkeletonBase = styled.div`
  background: linear-gradient(90deg, #ffe8a3 0%, #fff8e1 50%, #ffe8a3 100%);
  background-size: 800px 100%;
  animation: ${shimmer} 1.4s infinite linear;
  border-radius: 6px;
`;

const SkeletonCard = styled.div`
  flex: 0 0 200px;
  scroll-snap-align: start;
  background: transparent;
  border: none;
  overflow: hidden;

  @media (max-width: 600px) {
    flex: 0 0 46vw;
    max-width: 200px;
  }
`;

const SkeletonThumb = styled(SkeletonBase)`
  width: 100%;
  aspect-ratio: 3 / 4;
  border-radius: 0;
`;

const SkeletonBody = styled.div`
  padding: 0.6rem 0.75rem 0.75rem;
  display: flex;
  flex-direction: column;
  gap: 0.35rem;
  min-height: 3.4rem;
`;

const SkeletonBar = styled(SkeletonBase)`
  height: ${(p) => p.$height || '12px'};
  width: ${(p) => p.$width || '100%'};
`;

const resolveImage = (raw) => {
  if (!raw) return null;
  if (raw.startsWith('http')) return raw;
  const isDev =
    typeof window !== 'undefined' &&
    (window.location.hostname === 'localhost' ||
      window.location.hostname === '127.0.0.1');
  const base = isDev
    ? 'http://127.0.0.1:8080'
    : '';
  return `${base}${raw}`;
};

const pickImage = (target) => {
  if (!target) return null;
  if (target.primary_image) return resolveImage(target.primary_image);
  if (target.image_url) return resolveImage(target.image_url);
  if (target.images && target.images.length > 0) return resolveImage(target.images[0].image);
  return null;
};

const linkFor = (row) => {
  const catalog = row.catalog_item;
  if (catalog?.shop_type === 'artist' || row.artist_item) {
    return getArtistItemPublicPath(catalog || row.artist_item);
  }
  if (catalog?.shop_type === 'boutique' || row.boutique_item) {
    return getBoutiqueItemPublicPath(catalog || row.boutique_item);
  }
  if (catalog || row.designer_item) return getDesignerItemPublicPath(catalog || row.designer_item);
  if (row.item) return getMarketplaceItemDetailPath(row.item);
  return '/pre-owned-items';
};

const linkStateFor = (row) => {
  const catalog = row.catalog_item;
  if (catalog?.shop_type === 'artist' || row.artist_item) {
    return getListingLinkState(catalog || row.artist_item, 'artist_item');
  }
  if (catalog?.shop_type === 'boutique' || row.boutique_item) {
    return getListingLinkState(catalog || row.boutique_item, 'boutique_item');
  }
  if (catalog || row.designer_item) {
    return getListingLinkState(catalog || row.designer_item, 'designer_item');
  }
  if (row.item) return getListingLinkState(row.item, 'item');
  return undefined;
};

const SHOP_FEATURED_SCOPES = new Set([
  'designer_items',
  'boutique_items',
  'artist_items',
  'local_store_items',
]);

const HAND_PICKED_KEY = 'browse.handPickedByFlea';

const FeaturedStrip = ({
  scope,
  department = '',
  sectionTitle,
  includeCategory,
}) => {
  const { t } = useTranslation();
  const stripTitle = sectionTitle ?? t(HAND_PICKED_KEY);
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setError(false);
    (async () => {
      try {
        const data = await itemsAPI.getFeatured(scope, department || '');
        if (!cancelled) {
          let next = Array.isArray(data?.results) ? data.results : [];
          if (typeof includeCategory === 'function') {
            next = next.filter((row) => {
              const target =
                row.catalog_item || row.artist_item || row.designer_item || row.boutique_item || row.item;
              return target && includeCategory(target.category);
            });
          }
          setRows(next);
        }
      } catch {
        if (!cancelled) {
          setError(true);
          setRows([]);
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [scope, department, includeCategory]);

  // After load: hide if nothing curated.
  if (!loading && (error || rows.length === 0)) return null;

  return (
    <Section aria-label={stripTitle} aria-busy={loading || undefined}>
      <Header>
        <Title>{stripTitle}</Title>
      </Header>
      <Strip>
        {loading
          ? Array.from({ length: 5 }).map((_, i) => (
              <SkeletonCard key={`feat-skel-${i}`} aria-hidden>
                <SkeletonThumb />
                <SkeletonBody>
                  <SkeletonBar $height="12px" $width="90%" />
                  <SkeletonBar $height="14px" $width="40%" />
                </SkeletonBody>
              </SkeletonCard>
            ))
          : rows.map((row) => {
              const target = row.catalog_item || row.artist_item || row.designer_item || row.boutique_item || row.item;
              if (!target) return null;
              const favoriteTarget = getFavoriteTargetFromFeaturedRow(row);
              const img = resolveImage(row.featured_image) || pickImage(target);
              return (
                <Card key={row.id} to={linkFor(row)} state={linkStateFor(row)}>
                  <Thumb>
                    {favoriteTarget ? (
                      <ItemFavoriteButton
                        itemId={favoriteTarget.item.id}
                        favoriteKind={favoriteTarget.favoriteKind}
                        category={favoriteTarget.item.category}
                      />
                    ) : null}
                    {img ? <ThumbImg src={sizedImageUrl(img, 200) || img} alt="" loading="lazy" decoding="async" /> : '📦'}
                  </Thumb>
                  <Body>
                    <Price>{formatListingPriceLabel(target)}</Price>
                    <Meta>
                      <MetaBrand>{resolveListingBrand(target) || '—'}</MetaBrand>
                      {!SHOP_FEATURED_SCOPES.has(scope) && target.condition ? <span>· {target.condition}</span> : null}
                    </Meta>
                  </Body>
                </Card>
              );
            })}
      </Strip>
    </Section>
  );
};

export default FeaturedStrip;
